import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { Button, Card, CardBody, Col, Form, Row } from "react-bootstrap";
import * as yup from "yup";
import { IRoomName } from "../../shared/interfaces/room/IRoomName";
import { RoomNameService } from "./service";
import { useUnsavedChanges } from "../../shared/hooks/useUnsavedChanges";

const schema = yup.object().shape({
  name: yup.string().max(200, "maxLength").required("validation.required"),
});

const RoomNameForm = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<IRoomName>({
    resolver: yupResolver(schema) as any,
    defaultValues: isEdit
      ? async () => {
          try {
            return await RoomNameService.getSingle(+id!);
          } catch (e) {
            console.error(e);
          }
        }
      : {
          name: "",
        },
  });

  const { guardedNavigate, unsavedChangesModal } = useUnsavedChanges(isDirty);

  const onSubmit = async (data: IRoomName) => {
    try {
      if (isEdit) {
        await RoomNameService.update(data, +id!);
      } else {
        await RoomNameService.create(data);
      }
      SuccessToast(t(`roomName.${isEdit ? "successUpdate" : "successCreate"}`));
      navigate("/room-names");
      reset();
    } catch (error) {
      ErrorToast(error);
    }
  };

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="g-3">
                  <Col xl={4}>
                    <Form.Label className="required">
                      {t("roomItem.name")}
                    </Form.Label>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field: { onChange, value }, fieldState }) => (
                        <Form.Control
                          onChange={onChange}
                          value={value}
                          isInvalid={!!fieldState.error}
                          placeholder={t("roomItem.enterName")}
                        />
                      )}
                    />
                    {errors.name && (
                      <div className="invalid-feedback">
                        {errors.name.message === "validation.required"
                          ? t("validation.required")
                          : t(`validation.${errors.name.message}`, {
                              value: 200,
                            })}
                      </div>
                    )}
                  </Col>
                  <Col sm={12} className="d-flex gap-2">
                    <Button type="submit" variant="primary">
                      {t(`roomName.${isEdit ? "update" : "create"}`)}
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => guardedNavigate("/room-names")}>
                      {t("button.cancel")}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      {unsavedChangesModal}
    </React.Fragment>
  );
};

export default RoomNameForm;
